import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { getHealthApi, HealthRateLimited } from '../api/health'
import type { HealthData, HealthSummary } from '../api/health/types'
import { bannerKey, canSeeHealth, summaryFromData } from '../lib/health'
import { useClock } from './clock'
import { useSession } from './session'

export const SUMMARY_EVERY_MS = 60_000
export const PAGE_EVERY_MS = 60_000
const HIDDEN_KEY = 'cf-chat.health-banner-hidden'

function readHidden(): string {
  try {
    return localStorage.getItem(HIDDEN_KEY) ?? ''
  } catch {
    return ''
  }
}

function writeHidden(v: string): void {
  try {
    localStorage.setItem(HIDDEN_KEY, v)
  } catch {
    /* private mode: hidden for this page view only */
  }
}

const message = (e: unknown): string => (e instanceof Error ? e.message : String(e))

/**
 * สุขภาพระบบ (/health) + the "บริการล่ม" banner and the rail dot.
 * - Owner/head only: `start()` polls GET /api/v1/health/summary every 60 s (skipped while the tab is hidden);
 *   for staff it does nothing (the API would answer 403).
 * - The page keeps its full data here; a load or a manual run also refreshes the banner summary.
 * - "ซ่อน" on the banner hides it until the set of services that are down changes (localStorage).
 */
export const useHealth = defineStore('health', () => {
  const session = useSession()
  const allowed = computed(() => canSeeHealth(session.me?.role))

  // ---- banner / rail dot
  const summary = ref<HealthSummary | null>(null)
  const hidden = ref(readHidden())
  let users = 0
  let timer: ReturnType<typeof setInterval> | null = null
  let sumSeq = 0

  async function refreshSummary(): Promise<void> {
    if (!allowed.value) return
    const mine = ++sumSeq
    try {
      const got = await getHealthApi().summary()
      if (mine === sumSeq) summary.value = got
    } catch {
      /* keep the last one; the next poll tries again */
    }
  }

  function visible(): boolean {
    return typeof document === 'undefined' || document.visibilityState !== 'hidden'
  }

  /** App.vue (banner) mounts → start; unmounts → stop (reference counted). */
  function start(): void {
    users += 1
    if (timer || !allowed.value) return
    void refreshSummary()
    timer = setInterval(() => {
      if (visible()) void refreshSummary()
    }, SUMMARY_EVERY_MS)
  }

  function stop(): void {
    users = Math.max(0, users - 1)
    if (users > 0) return
    if (timer) clearInterval(timer)
    timer = null
  }

  const key = computed(() => bannerKey(summary.value))
  const showBanner = computed(() => allowed.value && (summary.value?.down ?? 0) > 0 && key.value !== hidden.value)
  const downCount = computed(() => (allowed.value ? (summary.value?.down ?? 0) : 0))

  function dismissBanner(): void {
    hidden.value = key.value
    writeHidden(key.value)
  }

  // ---- page
  const data = ref<HealthData | null>(null)
  const loading = ref(false)
  const running = ref(false)
  const error = ref<string | null>(null)
  /** one line under "ตรวจตอนนี้" (rate limit / already running / failed) */
  const runNote = ref<string | null>(null)
  /** ms epoch when a manual run is allowed again (from the server or a 429) */
  const retryAt = ref<number | null>(null)
  const savingSettings = ref(false)
  let seq = 0

  function take(d: HealthData): void {
    data.value = d
    summary.value = summaryFromData(d)
    const next = d.run.manual_next_at ? Date.parse(d.run.manual_next_at) : null
    // the server clock decides; keep a later local 429 deadline if we have one
    retryAt.value = next !== null && Number.isFinite(next) ? Math.max(next, retryAt.value ?? 0) : retryAt.value
  }

  async function load(): Promise<void> {
    if (!allowed.value) return
    const mine = ++seq
    loading.value = true
    try {
      const d = await getHealthApi().get()
      if (mine !== seq) return
      take(d)
      error.value = null
    } catch (e) {
      if (mine === seq) error.value = message(e)
    } finally {
      if (mine === seq) loading.value = false
    }
  }

  /** Seconds until "ตรวจตอนนี้" works again (0 = now). */
  const waitS = computed(() => {
    const at = retryAt.value
    if (at === null) return 0
    return Math.max(0, Math.ceil((at - useClock().now) / 1000))
  })

  async function run(): Promise<void> {
    if (!allowed.value || running.value) return
    running.value = true
    runNote.value = null
    const mine = ++seq // a run's result wins over an auto-refresh started meanwhile
    try {
      const d = await getHealthApi().run()
      take(d)
      error.value = null
    } catch (e) {
      if (e instanceof HealthRateLimited) {
        retryAt.value = useClock().now + e.retryAfterS * 1000
        runNote.value = e.message
      } else {
        runNote.value = message(e)
      }
    } finally {
      if (mine === seq) loading.value = false
      running.value = false
    }
  }

  async function setTelegram(on: boolean): Promise<boolean> {
    if (!data.value) return false
    savingSettings.value = true
    const before = data.value.settings
    data.value = { ...data.value, settings: { ...before, telegram_on: on } }
    try {
      const s = await getHealthApi().saveSettings({ telegram_on: on })
      if (data.value) data.value = { ...data.value, settings: { ...data.value.settings, ...s } }
      return true
    } catch (e) {
      if (data.value) data.value = { ...data.value, settings: before }
      runNote.value = `บันทึกการแจ้งเตือนไม่สำเร็จ: ${message(e)}`
      return false
    } finally {
      savingSettings.value = false
    }
  }

  /** Tests: forget everything (timers included). */
  function reset(): void {
    if (timer) clearInterval(timer)
    timer = null
    users = 0
    summary.value = null
    data.value = null
    error.value = null
    runNote.value = null
    retryAt.value = null
    hidden.value = readHidden()
  }

  return {
    allowed,
    summary,
    showBanner,
    downCount,
    bannerKey: key,
    start,
    stop,
    refreshSummary,
    dismissBanner,
    data,
    loading,
    running,
    error,
    runNote,
    retryAt,
    waitS,
    savingSettings,
    load,
    run,
    setTelegram,
    reset,
  }
})
